/**
* Component: AssetSpecification.jsx
* Description: This component is utilized for creating and updating the asset specification.
*/

import { Box, Grid, Typography, debounce } from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gridValues, mandatoryFields, updateAssetSpecification } from '../../redux/actions/assetActions';
import { BemsGraph, FemsGraph } from "../../utility/apiURLS";
import { getAuthToken } from "../../utility/tokenUtility";
import BottomButtons from "../commoncomponents/BottomButtons";
import ComponentHeader from "../commoncomponents/ComponentHeader";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import CustomTextField from "../commoncomponents/CustomTextField";
import assetCreateUpdateRepository from "../../api/repositories/assetCreateUpdateRepository";
import { validations } from "../../utility/constants/errors/messages";
import utils from "../../utility/utilities";
import AssetRepository from "../../api/repositories/assetRepository";
import PageLoader from "../commoncomponents/PageLoader";

const AssetSpecification = ({ setPage, page, buttonAttributes }) => {

  const dispatch = useDispatch();

  //get the manditory fileds for AssetSpecification from the redux
  const mandatoryFieldsAssetSpecification = useSelector((state) => state.mandatoryFields.mandatoryFieldsAssetSpecification);

  //check user choose to edit
  const selectedGridData = useSelector(
    (state) => state.gridValues.selectedGridData
  );

  //get the basicDetils form redux
  const basicDetails = useSelector((state) => state.basicDetails);
  //get the purchase details form redux
  const purchaseDetails = useSelector(state => state?.purchaseDetails);
  const showassetClassificationWhenRentalStatusIsYes = basicDetails?.rentalStatus?.label; //get rentalStatus user hase choosen 'Yes' or 'No'
  const showassetSpecification = basicDetails?.assetClassification?.label; //get which type of clasification user has choosen ex:building,location,asset etc.,

  //get the assetSpecification form redux
  const assetSpecification = useSelector(
    (state) => state.assetSpecification
  );

  const [assetStatusOptions, setAssetStatusOptions] = useState([]);
  const [isFilledAllMandatoryField, setIsFilledAllMandatoryField] = useState(false);
  const [isDataChanged,setIsDataChanged]=useState(false);

  const [isBEMS, setIsBEMS] = useState(false); //set FEMS or BEMS here based on user choose
  const assetCategoryType = useSelector((state) => state?.serviceType?.assetType);
  const assetType = assetCategoryType === "/assetsFEM" ? FemsGraph : BemsGraph;

  const [error, setError] = useState({
    make: false,
    brand: false,
    model: false,
    serialNo: false,
    manufacturer: false,
    lifeSpan: false,
  });

  const [errorMessage, setErrorMessage] = useState({
    make: '',
    brand: '',
    model: '',
    serialNo: '',
    manufacturer: '',
    lifeSpan: '',
  });

  const [assetSpecificationdata, setAssetSpecificationdata] = useState({ ...assetSpecification } ||
  {
    make: '',
    brand: '',
    model: '',
    serialNo: '',
    version:'',
    engineNo: '',
    chassisNo: '',
    manufacturer: '',
    registrationNo: '',
    capacity: '',
    assetStatus: { id: 1, key: 'option1', label: 'Functioning ', isvisible: false },
    lifeSpan: '',
    buildupArea: '',
    footPrint: '',
    landArea: '',
  });
  const [initialAssetSpecificationData, setInitialAssetSpecificationData] = useState({});
  const [isLoading, setIsLoading] = useState(false)
  // get AssetStatus Options
  const assetStatusOptionsAPI = async () => {
    try {
      setIsLoading(true);
      const token = getAuthToken("id_token"); // Example: Retrieve token from localStorage
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      };
      const response = await axios.get(`${assetType}/api/CommonLov/GetCommonLovList`, config);
      const femOrbem = assetType === FemsGraph ? "fem" : "bem";
      const FemORBem = assetType === FemsGraph ? "Fem" : "Bem";
      setAssetStatusOptions(response.data?.[`${femOrbem}assetstatuses`]?.map((item) => ({
        id: item?.id,
        key: `option${item.id}`,
        label: item?.assetstatus,
        isvisible: item?.isvisible
      })) || []);

      if (selectedGridData.isEdit && !assetSpecificationdata?.isDataSetInEditMode) {
        const assetstatusId = selectedGridData?.values?.[`${femOrbem}assetstatusId`] || selectedGridData?.values?.[`${FemORBem}assetstatusId`];
        const assetstatusoption = response?.data?.[`${femOrbem}assetstatuses`].find(item => item.id === assetstatusId);
        const assetstatus = { id: assetstatusoption?.id, label: assetstatusoption?.assetstatus, isvisible: assetstatusoption?.isvisible };
        // make the default value when none of the id matches
        setAssetSpecificationdata((prev) => {
          return { ...prev, assetStatus: assetstatus?.id ? assetstatus : { id: 1, label: 'Functioning ', isvisible: true } };
        });
        setInitialAssetSpecificationData((prev) => {
          return {
            ...prev,
            assetStatus: assetstatus?.id ? assetstatus : { id: 1, label: 'Functioning ', isvisible: true }
          }
        })
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (assetCategoryType === "/assetsBEM") {
      setIsBEMS(true);
    }
    assetStatusOptionsAPI(); //Rendering the AssetStatus Options
    setInitialAssetSpecificationData(assetSpecificationdata);
  }, [])

  useEffect(() => {
    if (selectedGridData && selectedGridData?.isEdit && !assetSpecificationdata?.isDataSetInEditMode) {
      setIsLoading(true);
      const make = selectedGridData?.values?.make;
      const brand = selectedGridData?.values?.brand;
      const model = selectedGridData?.values?.model;
      const serialNo = selectedGridData?.values?.serialno;
      const version = selectedGridData?.values?.version;
      const engineNo = selectedGridData?.values?.engineno;
      const chassisNo = selectedGridData?.values?.chassisno;
      const registerationNo = selectedGridData?.values?.registerationno;
      const manufacturer = selectedGridData?.values?.manufacturer;
      const lifeSpan = selectedGridData?.values?.lifespan;
      const buildupArea = selectedGridData?.values?.builtuparea;
      const footPrint = selectedGridData?.values?.footprint;
      const landArea = selectedGridData?.values?.landarea;
      const capacity = selectedGridData?.values?.capacity;
      //assetStatus value is set in assetStatusOptionsAPI refer that
      setInitialAssetSpecificationData({
        ...assetSpecificationdata,
        make: make,
        brand: brand,
        model: model,
        serialNo: serialNo,
        version:version,
        engineNo: engineNo,
        chassisNo: chassisNo,
        manufacturer: manufacturer,
        registrationNo: registerationNo,
        lifeSpan: lifeSpan,
        buildupArea: buildupArea,
        footPrint: footPrint,
        landArea: landArea,
        isDataSetInEditMode: true, //set this value true so that we can use it in edit mode only for not rendering the sideEffect of edit mode
        capacity: capacity,
      })
      setAssetSpecificationdata((prev) => {
        return {
          ...prev,
          make: make,
          brand: brand,
          model: model,
          serialNo: serialNo,
          version:version,
          engineNo: engineNo,
          chassisNo: chassisNo,
          manufacturer: manufacturer,
          registrationNo: registerationNo,
          lifeSpan: lifeSpan,
          buildupArea: buildupArea,
          footPrint: footPrint,
          landArea: landArea,
          isDataSetInEditMode: true, //set this value true so that we can use it in edit mode only for not rendering the sideEffect of edit mode
          capacity: capacity,
          //femassetstatus is set in assetStatusOptionsAPI check that
        }
      });
      setIsLoading(false);
    }
  }, [selectedGridData.isEdit]);

  // checking the if all mandatory fields are filled or not
  const memoizedDetails = useMemo(() => ({
    make: assetSpecificationdata?.make || '',
    brand: assetSpecificationdata?.brand || '',
    model: assetSpecificationdata?.model || '',
    serialNo: assetSpecificationdata?.serialNo || '',
    manufacturer: assetSpecificationdata?.manufacturer || '',
    lifeSpan: assetSpecificationdata?.lifeSpan || ''
  }), [
    assetSpecificationdata?.make,
    assetSpecificationdata?.brand,
    assetSpecificationdata?.model,
    assetSpecificationdata?.serialNo,
    assetSpecificationdata?.manufacturer,
    assetSpecificationdata?.lifeSpan
  ]);

  const checkMandatoryValueFilled = useCallback(debounce((
    make,
    brand,
    model,
    serialNo,
    manufacturer,
    lifeSpan
  ) => {
    const updateField = (fieldName, value) => {
      const field = mandatoryFieldsAssetSpecification.filter((item) => item.name === fieldName);
      return field.map((item) => ({
        ...item,
        valueFilled: item?.name === fieldName ? !!value : value
      }));
    };

    const updateMake = updateField('make', make);
    const updateBrand = updateField('brand', brand);
    const updateModel = updateField('model', model);
    const updateSerialno = updateField('serialNo', serialNo);
    const updateManufacturer = updateField('manufacturer', manufacturer);
    const updateLifespan = updateField('lifeSpan', lifeSpan);

    const updateMandataryfields = [
      ...updateMake,
      ...updateBrand,
      ...updateModel,
      ...updateSerialno,
      ...updateManufacturer,
      ...updateLifespan
    ];

    if (updateMandataryfields?.every(item => item !== undefined)) {
      dispatch(mandatoryFields(updateMandataryfields, "UPDATE_ASSET_SPECIFICATION_MANDATORY_FIELDS"));
    }
  }, 300), [dispatch]);

  useEffect(() => {
    const {
      make,
      brand,
      model,
      serialNo,
      manufacturer,
      lifeSpan
    } = memoizedDetails;

    checkMandatoryValueFilled(
      make.trim(),
      brand.trim(),
      model.trim(),
      serialNo.trim(),
      manufacturer.trim(),
      lifeSpan
    );
  }, [
    memoizedDetails,
    checkMandatoryValueFilled
  ]);


  //prop value that holds all details about current page,buttons,header,etc., refer AssetMain.jsx for more info
  const findHeaderContent = buttonAttributes.find(
    (item) => item?.currentPageName === page
  );

  const handleOnChange = (value, name, isMandatoryField) => {
    // Handle the change event with the name and value
    setAssetSpecificationdata((prev) => {
      return {
        ...prev,
        [name]: value
      }
    })
    setIsDataChanged(true);
    if (isMandatoryField) {
      setError((prev) => {
        const updatedError = {
          ...prev,
          [name]: false
        };

        const isAllFalse = Object.values(updatedError).every((value) => value === false);

        if (isAllFalse) {
          setIsFilledAllMandatoryField(false);
        }

        return updatedError;
      });
      setErrorMessage((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    if (isMandatoryField) {
      if (name !== 'lifeSpan' && name !== 'buildupArea' && name !== 'footPrint' && name !== 'assetStatus') {
        if (value) {
          setError((prev) => ({ ...prev, [name]: false }));
          setErrorMessage((prev) => ({ ...prev, [name]: "" }));
        };
      };
    };

  };

  //function to validate required fields
  const validateRequiredFileds = () => {
    let isValid = true;
    mandatoryFieldsAssetSpecification?.forEach((item) => {
      if (item.name === 'lifeSpan') {
        if (item.isVisible && !item.valueFilled) {
          setError((prev) => ({
            ...prev,
            [item.name]: true,
          }));
          setErrorMessage((prev) => ({
            ...prev,
            [item.name]: `${item.type} is required`,
          }));
          isValid = false;
        }
      } else {
        if (item.isVisible && !item.valueFilled) {
          setError((prev) => ({
            ...prev,
            [item.name]: true,
          }));
          if (errorMessage?.[item.name] === '') {
            setErrorMessage((prev) => ({
              ...prev,
              [item.name]: `${item.type} is required`,
            }));
          }
          isValid = false;
        }
      }
    });
    return isValid;
  };

  //function to clear inputs
  const clearInputs = () => {
    const capacity = assetSpecificationdata?.capacity;
    const assetStatus = assetSpecificationdata?.assetStatus;
    const lifeSpan = assetSpecificationdata?.lifeSpan;
    const commonFields = { capacity, assetStatus, lifeSpan };
    let updateFields;

    if (basicDetails.rentalStatus?.label === "No" && basicDetails.assetClassification?.label !== "Asset") {
      if (basicDetails.assetClassification?.label === "System") {
        const clearAllhiddenFieldsValues = { make: '', brand: '', model: '', serialNo: '', engineNo: '', chassisNo: '', manufacturer: '', registrationNo: '', depreciationValue: 0, buildupArea: 0.00, footPrint: 0.00, landArea: 0.00 };
        updateFields = { ...commonFields, ...clearAllhiddenFieldsValues };
      } else if (basicDetails.assetClassification?.label === "Building") {
        const buildupArea = assetSpecificationdata?.buildupArea;
        const footPrint = assetSpecificationdata?.footPrint;
        const buildingFields = { buildupArea, footPrint };
        const clearAllhiddenFieldsValues = { make: '', brand: '', model: '', serialNo: '', engineNo: '', chassisNo: '', manufacturer: '', registrationNo: '', depreciationValue: 0, landArea: 0.00 };
        updateFields = { ...commonFields, ...buildingFields, ...clearAllhiddenFieldsValues };
      } else {
        const locationFields = assetSpecificationdata?.landArea;
        const clearAllhiddenFieldsValues = { make: '', brand: '', model: '', serialNo: '', engineNo: '', chassisNo: '', manufacturer: '', registrationNo: '', depreciationValue: 0, buildupArea: 0.00, footPrint: 0.00 };
        updateFields = { ...commonFields, ...locationFields, ...clearAllhiddenFieldsValues };
      }
    }
    return updateFields;
  };

  function mapKeyToInitialData(key) {
    // Define a mapping between the current key and the initial data key
    const keyMap = {
      make: 'make',
      brand: 'brand',
      model: 'model',
      serialno: 'serialNo',
      engineno: 'engineNo',
      chassisno: 'chassisNo',
      registerationno: 'registrationNo',
      manufacturer: 'manufacturer',
      lifespan: 'lifeSpan',
      builtuparea: 'buildupArea',
      footprint: 'footPrint',
      landarea: 'landArea',
    };

    return keyMap[key] || key; // If a mapping exists, return the mapped key, else return the original key
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = validateRequiredFileds();
    const clearedFields = clearInputs();
    if (isValid) {
      const selectedData = selectedGridData?.values;
      const commonParameters = {
        make: clearedFields?.make ?? assetSpecificationdata?.make ?? '',
        brand: clearedFields?.brand ?? assetSpecificationdata?.brand ?? '',
        model: clearedFields?.model ?? assetSpecificationdata?.model ?? '',
        serialno: clearedFields?.serialNo ?? assetSpecificationdata?.serialNo ?? '',
        version: clearedFields?.version ?? assetSpecificationdata?.version ?? '',
        engineno: clearedFields?.engineNo ?? assetSpecificationdata?.engineNo ?? '',
        chassisno: clearedFields?.chassisNo ?? assetSpecificationdata?.chassisNo ?? '',
        registerationno: clearedFields?.registrationNo ?? assetSpecificationdata?.registrationNo ?? '',
        manufacturer: clearedFields?.manufacturer ?? assetSpecificationdata?.manufacturer ?? '',
        lifespan: clearedFields?.lifeSpan ?? assetSpecificationdata?.lifeSpan ?? '',
        builtuparea: clearedFields?.buildupArea ?? assetSpecificationdata?.buildupArea ?? '',
        footprint: clearedFields?.footPrint ?? assetSpecificationdata?.footPrint ?? '',
        landarea: clearedFields?.landArea ?? assetSpecificationdata?.landArea ?? '',
        capacity: clearedFields?.capacity ?? assetSpecificationdata?.capacity ?? '',
      };
      const FemsParameter = {
        FemassetstatusId: clearedFields?.femAssetStatusId ?? assetSpecificationdata?.assetStatus?.id ?? '',
      };
      const BemsParameter = {
        BemassetstatusId: clearedFields?.BemAssetStatusId ?? assetSpecificationdata?.assetStatus?.id ?? '',
      }
      let assetSpecificationParameters = { ...commonParameters, }
      const type = assetType === FemsGraph ? "FEMS" : "BEMS";
      type === "FEMS" ? assetSpecificationParameters = { ...assetSpecificationParameters, ...FemsParameter } : assetSpecificationParameters = { ...assetSpecificationParameters, ...BemsParameter };
      // Check if any of the assetSpecificationdata values have been modified
      // If any of the values have been modified, make the API call
      const isDataChanged = Object.keys(assetSpecificationdata).some((key) => {
        const initialKey = mapKeyToInitialData(key);
        return assetSpecificationdata[key] !== initialAssetSpecificationData[initialKey];
      });
      if (isDataChanged) {
        try {
          const processingpercentage = selectedData?.processingpercentage > 50 ? selectedData?.processingpercentage : 50;
          const isEditMode = purchaseDetails?.isAssetCreation ? false : true;
          const changedkeys = AssetRepository.compareObjects(initialAssetSpecificationData, assetSpecificationdata);
          const response = assetCreateUpdateRepository?.updateAsset(selectedData, { ...assetSpecificationParameters, processingpercentage: processingpercentage }, type, isEditMode, changedkeys);
          response.then((res) => {
            //set the Testing and commissioning related data here
            dispatch(updateAssetSpecification("UPDATE_ASSET_SPECIFICATION", assetSpecificationdata));
            const purchaseDetailsField = {
              warrantystartdate: purchaseDetails?.warrantyStartDate,
              warrantyenddate: purchaseDetails?.warrantyEndDate,
              commissioningdate: purchaseDetails?.commissioningDate,
              purchaseorderno: purchaseDetails?.purchaseOrderNo,
              purchasecost: purchaseDetails?.purchaseCost,
              underWaranty: purchaseDetails?.underWarranty,
              vendorid: purchaseDetails?.vendor?.id
            };
            const mergedDetails = { ...res, ...purchaseDetailsField };
            if (purchaseDetails?.isTCFieldSet) {
              dispatch(gridValues({ values: mergedDetails, isEdit: true, isView: false }));
            } else {
              dispatch(gridValues({ values: res, isEdit: true, isView: false }));
            }
            setPage("Purchase/Warranty Details");
            dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));

          });
        } catch (error) {
          console.log(error);
        }
      } else {
        // No data has changed and processing percentange less than 50 make API call
        //otherwise do not make API call
        const processingpercentage = selectedData?.processingpercentage > 50 ? selectedData?.processingpercentage : 50;
        if (processingpercentage <= 50) {
          const response = assetCreateUpdateRepository?.updateAsset(selectedData, { ...assetSpecificationParameters, processingpercentage: processingpercentage }, type);
          response.then((res) => {
            //set the Testing and commissioning related data here
            dispatch(updateAssetSpecification("UPDATE_ASSET_SPECIFICATION", assetSpecificationdata));
            const purchaseDetailsField = {
              warrantystartdate: purchaseDetails?.warrantyStartDate,
              warrantyenddate: purchaseDetails?.warrantyEndDate,
              commissioningdate: purchaseDetails?.commissioningDate,
              purchaseorderno: purchaseDetails?.purchaseOrderNo,
              purchasecost: purchaseDetails?.purchaseCost,
              underWaranty: purchaseDetails?.underWarranty,
              vendorid: purchaseDetails?.vendor?.id
            };
            const mergedDetails = { ...res, ...purchaseDetailsField };
            if (purchaseDetails?.isTCFieldSet) {
              dispatch(gridValues({ values: mergedDetails, isEdit: true, isView: false }));
            } else {
              dispatch(gridValues({ values: res, isEdit: true, isView: false }));
            }
            setPage("Purchase/Warranty Details");
            dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));

          });
        } else {
          setPage("Purchase/Warranty Details");
          dispatch(mandatoryFields(false, "UPDATE_MANDATORY_REQUIRED_ENABLED"));

        }
      }
    } else {
      setIsFilledAllMandatoryField(true);
    }
  };

  const assetValue = basicDetails?.assetValue || selectedGridData?.values?.assetNo || '';

  return (
    <>
      <PageLoader open={isLoading} />
      <ComponentHeader
        mainHeader={findHeaderContent.mainHeader}
        subHeader={findHeaderContent.subHeader}
        assetvalue={assetValue}
        requiredButton={findHeaderContent.requiredButton}
        statusType={selectedGridData?.values?.isauthorized ? 'authorized' : 'unauthorized'}
      />
      <Box mt={{ xs: 1, md: 2 }}>
        <form onSubmit={handleSubmit} style={{ position: "relative" }} noValidate>
          {isFilledAllMandatoryField && <Typography sx={{ color: 'error.main' }}>Please Fill All Mandatory Fields(*)</Typography>}
          <Grid container spacing={2}>
            {((showassetClassificationWhenRentalStatusIsYes === "No" && showassetSpecification === 'Asset') || showassetClassificationWhenRentalStatusIsYes === "Yes") && (
              <>
                {/* Row 1 */}
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Make"
                    id="finance-asset-no"
                    error={error?.make}
                    errorMessage={errorMessage?.make}
                    isMandatoryField={true}
                    name="make"
                    onChange={handleOnChange}
                    value={assetSpecificationdata?.make || ''}
                  />
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Brand"
                    id="brand"
                    error={error?.brand}
                    errorMessage={errorMessage?.brand}
                    isMandatoryField={true}
                    name="brand"
                    onChange={handleOnChange}
                    value={assetSpecificationdata?.brand || ''}
                  />
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Model"
                    id="model"
                    error={error?.model}
                    errorMessage={errorMessage?.model}
                    isMandatoryField={true}
                    name="model"
                    onChange={handleOnChange}
                    value={assetSpecificationdata?.model || ''}
                  />
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Serial No"
                    id="serial-no"
                    error={error?.serialNo}
                    errorMessage={errorMessage?.serialNo}
                    isMandatoryField={true}
                    name="serialNo"
                    onChange={handleOnChange}
                    value={assetSpecificationdata?.serialNo || ''}
                  />
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Asset Version"
                    id="version"
                    // error={error?.brand}
                    // errorMessage={errorMessage?.brand}
                    // isMandatoryField={true}
                    name="version"
                    onChange={handleOnChange}
                    value={assetSpecificationdata?.version || ''}
                    textAlign="right"
                  />
                </Grid>
                {!isBEMS && (
                  <>
                    {/* Row 2 */}
                    <Grid item xs={12} md={4} sm={6}>
                      <CustomTextField
                        label="Engine No."
                        id="engine-no"
                        name="engineNo"
                        onChange={handleOnChange}
                        value={assetSpecificationdata?.engineNo || ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sm={6}>
                      <CustomTextField
                        label="Chassis No."
                        id="chassis-no"
                        name="chassisNo"
                        onChange={handleOnChange}
                        value={assetSpecificationdata?.chassisNo || ''}
                      />
                    </Grid>
                    <Grid item xs={12} md={4} sm={6}>
                      <CustomTextField
                        label="Registration No."
                        id="registration-no"
                        name="registrationNo"
                        onChange={handleOnChange}
                        value={assetSpecificationdata?.registrationNo || ''}
                      />
                    </Grid>
                  </>
                )}
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Manufacturer"
                    id="manufacturer"
                    error={error?.manufacturer}
                    errorMessage={errorMessage?.manufacturer}
                    isMandatoryField={true}
                    name="manufacturer"
                    onChange={handleOnChange}
                    value={assetSpecificationdata?.manufacturer || ''}
                  />
                </Grid>
              </>
            )}

            {showassetSpecification === 'Building' && (
              <>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Built-up Area (Sq.m)"
                    id="buildupArea"
                    onlyNumbers={true}
                    //for building not metioned this field is manditory
                    // isMandatoryField={true}
                    name="buildupArea"
                    value={assetSpecificationdata?.buildupArea || ''}
                    onChange={handleOnChange}
                    textAlign="right"
                  />
                </Grid>
                <Grid item xs={12} md={4} sm={6}>
                  <CustomTextField
                    label="Foot Print"
                    id="footPrint"
                    onlyNumbers={true}
                    //for building not metioned this field is manditory
                    // isMandatoryField={true}
                    name="footPrint"
                    onChange={handleOnChange}
                    value={assetSpecificationdata?.footPrint || ''}
                      textAlign="right"
                  />
                </Grid>
              </>

            )}

            {showassetSpecification === 'Location' && (
              <Grid item xs={12} md={4} sm={6}>
                <CustomTextField
                  label="Land Area (Sq.m)"
                  id="landArea"
                  //for location not metioned this field is manditory
                  // isMandatoryField={true}
                  name="landArea"
                  onChange={handleOnChange}
                  value={assetSpecificationdata?.landArea || ''}
                  textAlign="right"
                />
              </Grid>
            )}

            <Grid item xs={12} md={4} sm={6}>
              <CustomTextField
                label="Capacity"
                id="capacity"
                name="capacity"
                value={assetSpecificationdata?.capacity || ''}
                onChange={handleOnChange}
                textAlign="right"
              />
            </Grid>
            <Grid item xs={12} md={4} sm={6}>
              <CustomSelectInput
                id="asset-classification-label"
                label="Asset Status"
                name="assetStatus"
                value={assetSpecificationdata?.assetStatus || ''}
                options={assetStatusOptions}
                onChange={handleOnChange}
              />
            </Grid>
            <Grid item xs={12} md={4} sm={6}>
              <CustomTextField
                label="Life Span(yrs)"
                id="lifeSpan"
                //Loaner/Rental Asset selected ‘Yes’, then make it non mandatory for all its  classifications.
                //Lifspan is mandatory only for Equipment(ASSET)
                error={error?.lifeSpan}
                errorMessage={errorMessage?.lifeSpan}
                isMandatoryField={basicDetails?.rentalStatus?.label === "Yes" ? false : ((showassetSpecification === 'Asset') || (showassetSpecification === 'System')) ? true : false}
                onlyNumbers={true}
                name="lifeSpan"
                value={assetSpecificationdata?.lifeSpan || ''}
                onChange={handleOnChange}
                textAlign="right"
              />
            </Grid>

          </Grid>
          <Box>
            <BottomButtons isDataChanged={isDataChanged} page={page} buttonAttributes={buttonAttributes} params={assetSpecificationdata} notUpdateRedux={purchaseDetails?.isAssetCreation} />
          </Box>
        </form>
      </Box>
    </>
  );
};

export default AssetSpecification;
